import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Info, Loader2, Play, Square, Terminal, Trash2, X } from 'lucide-react';
import {
  DEFAULT_VERIFIER_PROMPT_MODEL,
  VERIFIER_PROMPT_MODELS,
  VerifierRunPhase,
  VerifierRunStatus,
  VerifierStreamEvent,
  VerifierUserFacingError,
} from '../../types/verifier';

interface VerifierOutputPanelProps {
  canRun: boolean;
  entrypoint: string | null;
  error: string | null;
  errorDetails?: VerifierUserFacingError | null;
  events: VerifierStreamEvent[];
  onClear: () => void;
  onRun: (model?: string) => void;
  onStop: () => void;
  phase?: VerifierRunPhase;
  running: boolean;
  snapshotCapturedAt: Date | null;
  status: VerifierRunStatus;
}

const VERIFIER_PACKAGES = [
  'Flask==3.1.0',
  'gunicorn==23.0.0',
  'numpy==2.4.2',
  'pandas==3.0.1',
  'pyscf==2.12.1',
  'qiskit==2.3.1',
  'qutip==5.2.3',
  'rdkit==2025.9.6',
  'scikit-learn==1.8.0',
  'scipy==1.17.1',
  'sympy==1.14.0',
  'ViennaRNA==2.7.2',
];

function formatDuration(durationMs: number): string {
  if (durationMs < 1000) {
    return `${durationMs} ms`;
  }

  return `${(durationMs / 1000).toFixed(durationMs < 10000 ? 1 : 0)} s`;
}

function formatElapsedDuration(durationMs: number): string {
  const totalSeconds = Math.max(Math.floor(durationMs / 1000), 0);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes < 1) {
    return `${seconds}s`;
  }

  return `${minutes}m ${seconds}s`;
}

function getPromptWaitSummary(durationMs: number): string {
  if (durationMs < 15000) {
    return 'The verifier is running prompt.tex through the model before the verifier starts.';
  }

  if (durationMs < 45000) {
    return 'Still waiting for the AI response. Larger prompts can take a while to finish.';
  }

  return 'This is taking longer than usual. You can keep waiting, or stop and retry the run.';
}

function renderEventLabel(event: VerifierStreamEvent): string | null {
  if (event.type === 'started') {
    return `[started] ${event.entrypoint} (${event.runId})`;
  }

  if (event.type === 'exit') {
    if (event.aborted) {
      return `[exit] aborted after ${formatDuration(event.durationMs)}`;
    }

    if (event.timedOut) {
      return `[exit] timed out after ${formatDuration(event.durationMs)}`;
    }

    if (typeof event.exitCode === 'number') {
      return `[exit] code ${event.exitCode} in ${formatDuration(event.durationMs)}`;
    }

    if (typeof event.signal === 'number') {
      return `[exit] signal ${event.signal} in ${formatDuration(event.durationMs)}`;
    }

    return `[exit] finished in ${formatDuration(event.durationMs)}`;
  }

  return null;
}

function getStatusClassName(status: VerifierRunStatus): string {
  switch (status) {
    case 'success':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    case 'failed':
    case 'timed_out':
    case 'aborted':
      return 'border-rose-200 bg-rose-50 text-rose-700';
    case 'starting':
    case 'running':
      return 'border-blue-200 bg-blue-50 text-blue-700';
    default:
      return 'border-slate-200 bg-slate-100 text-slate-700';
  }
}

function getStatusLabel(status: VerifierRunStatus, phase: VerifierRunPhase): string {
  if (status === 'starting' && phase === 'prompt') {
    return 'Waiting On AI';
  }

  if (status === 'starting' && phase === 'verifier') {
    return 'Preparing';
  }

  switch (status) {
    case 'starting':
      return 'Starting';
    case 'running':
      return 'Running';
    case 'success':
      return 'Success';
    case 'failed':
      return 'Failed';
    case 'timed_out':
      return 'Timed Out';
    case 'aborted':
      return 'Aborted';
    default:
      return 'Idle';
  }
}

export function VerifierOutputPanel({
  canRun,
  entrypoint,
  error,
  errorDetails,
  events,
  onClear,
  onRun,
  onStop,
  phase = 'idle',
  running,
  snapshotCapturedAt,
  status,
}: VerifierOutputPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showRuntimeHelp, setShowRuntimeHelp] = useState(false);
  const [currentTimeMs, setCurrentTimeMs] = useState(() => Date.now());
  const [selectedModel, setSelectedModel] = useState(DEFAULT_VERIFIER_PROMPT_MODEL);

  useEffect(() => {
    const nextFrame = window.requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });

    return () => window.cancelAnimationFrame(nextFrame);
  }, [events]);

  useEffect(() => {
    if (status !== 'starting' || phase !== 'prompt' || !snapshotCapturedAt) {
      return undefined;
    }

    setCurrentTimeMs(Date.now());
    const intervalId = window.setInterval(() => {
      setCurrentTimeMs(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [phase, snapshotCapturedAt, status]);

  const promptWaitMs = useMemo(() => {
    if (status !== 'starting' || phase !== 'prompt' || !snapshotCapturedAt) {
      return 0;
    }

    return Math.max(currentTimeMs - snapshotCapturedAt.getTime(), 0);
  }, [currentTimeMs, phase, snapshotCapturedAt, status]);
  const statusLabel = useMemo(() => getStatusLabel(status, phase), [phase, status]);

  return (
    <>
      <div className="flex h-full flex-col bg-[#0f172a] text-slate-100">
        <div className="border-b border-slate-800 bg-[#111c34] px-4 py-3">
          <div className="mb-2">
            <div className="flex items-center justify-between gap-2 text-sm font-semibold text-slate-100">
              <div className="flex items-center gap-2">
                <Terminal className="h-4 w-4 text-cyan-300" />
                <span className="truncate">Verifier Output</span>
              </div>
              <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${getStatusClassName(status)}`}>
                {statusLabel}
              </span>
            </div>
            <div className="mt-1 text-xs text-slate-400">
              {entrypoint
                ? phase === 'prompt' && status === 'starting'
                  ? `Preparing verifier input from prompt.tex before starting ${entrypoint}.`
                  : `Running ${entrypoint} inside the isolated verifier service.`
                : 'Select a .py or .lean file to run.'}
              {snapshotCapturedAt ? ` Snapshot captured at ${snapshotCapturedAt.toLocaleTimeString()}.` : ''}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={selectedModel}
              onChange={(event) => setSelectedModel(event.target.value as typeof selectedModel)}
              disabled={running}
              className="rounded-md border border-slate-700 bg-slate-800 px-2 py-1.5 text-xs text-slate-200 disabled:opacity-50"
            >
              {VERIFIER_PROMPT_MODELS.map((model) => (
                <option key={model} value={model}>
                  {model.split('/').pop()}
                </option>
              ))}
            </select>
            {running ? (
              <button
                type="button"
                onClick={onStop}
                className="inline-flex items-center gap-1.5 rounded-md border border-rose-300 bg-rose-50 px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-100"
              >
                <Square className="h-4 w-4" />
                Stop
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onRun(selectedModel)}
                disabled={!canRun || !entrypoint}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                {status === 'starting' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                Run
              </button>
            )}
            <button
              type="button"
              onClick={onClear}
              disabled={running || events.length === 0}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-1.5 text-sm font-medium text-slate-200 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              Clear
            </button>
            <button
              type="button"
              onClick={() => setShowRuntimeHelp(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-cyan-700/70 px-3 py-1.5 text-sm font-medium text-cyan-100 hover:bg-slate-800"
              aria-label="Verifier runtime help"
            >
              <Info className="h-4 w-4" />
              Help
            </button>
          </div>
        </div>

        {!errorDetails && error && (
          <div className="border-b border-rose-900/40 bg-rose-950/40 px-4 py-2 text-sm text-rose-200">
            {error}
          </div>
        )}

        {errorDetails && (
          <div className="border-b border-rose-900/40 bg-rose-950/40 px-4 py-3 text-rose-100">
            <div className="text-sm font-semibold">{errorDetails.title}</div>
            <div className="mt-1 text-sm text-rose-200">{errorDetails.message}</div>
            {errorDetails.guidance && (
              <div className="mt-2 text-xs text-rose-100/90">{errorDetails.guidance}</div>
            )}
          </div>
        )}

        {!canRun && entrypoint && (
          <div className="border-b border-amber-900/30 bg-amber-950/30 px-4 py-2 text-sm text-amber-100">
            You need access to this project or reviewer, admin, or owner privileges to run this verifier.
          </div>
        )}

        {status === 'starting' && phase === 'prompt' && !errorDetails && (
          <div className="border-b border-cyan-900/40 bg-cyan-950/30 px-4 py-3 text-cyan-50">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Loader2 className="h-4 w-4 animate-spin text-cyan-300" />
              <span>Waiting for AI response</span>
            </div>
            <div className="mt-1 text-sm text-cyan-100">
              {getPromptWaitSummary(promptWaitMs)}
            </div>
            <div className="mt-2 text-xs text-cyan-200/90">
              Elapsed {formatElapsedDuration(promptWaitMs)}.
            </div>
          </div>
        )}

        <div
          ref={scrollRef}
          className="flex-1 overflow-auto px-4 py-3 font-mono text-sm leading-6"
        >
          {events.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-700/80 bg-slate-900/50 px-4 py-5 text-sm text-slate-400">
              Output from `stdout`, `stderr`, and verifier lifecycle events will stream here.
            </div>
          ) : (
            <div className="space-y-1">
              {events.map((event, index) => {
                const label = renderEventLabel(event);

                if (label) {
                  return (
                    <div
                      key={`${event.type}-${index}`}
                      className={`whitespace-pre-wrap ${
                        event.type === 'exit'
                          ? status === 'success'
                            ? 'text-emerald-300'
                            : 'text-rose-300'
                          : 'text-cyan-300'
                      }`}
                    >
                      {label}
                    </div>
                  );
                }

                if (event.type !== 'stdout' && event.type !== 'stderr' && event.type !== 'system') {
                  return null;
                }

                return (
                  <div
                    key={`${event.type}-${index}`}
                    className={`whitespace-pre-wrap ${
                      event.type === 'stderr'
                        ? 'text-rose-300'
                        : event.type === 'system'
                        ? 'text-slate-400'
                        : 'text-slate-100'
                    }`}
                  >
                    {event.text}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showRuntimeHelp && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4"
          onClick={() => setShowRuntimeHelp(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="verifier-runtime-help-title"
            className="w-full max-w-2xl rounded-xl border border-slate-200 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
              <div>
                <h2 id="verifier-runtime-help-title" className="text-lg font-semibold text-slate-900">
                  Verifier Runtime Help
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  Use this as the stable contract for what your <code className="rounded bg-slate-100 px-1 py-0.5 text-[13px]">verifier.py</code> can import and read.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowRuntimeHelp(false)}
                className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close verifier runtime help"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-6 px-5 py-4 text-sm text-slate-700">
              <section>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-900">Bundled Packages</h3>
                <p className="mt-2">
                  The verifier service currently runs on Python 3.12 and ships with these exact pinned top-level packages:
                </p>
                <div className="mt-3 rounded-lg bg-slate-950 px-4 py-3 font-mono text-sm text-slate-100">
                  {VERIFIER_PACKAGES.map((pkg) => (
                    <div key={pkg}>{pkg}</div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Treat this list as the supported runtime surface. Transitive dependencies may exist, but they are not part of the contract for verifier code.
                </p>
              </section>

              <section>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-900">Using stdin</h3>
                <p className="mt-2">
                  When you run a verifier, the app first runs <code className="rounded bg-slate-100 px-1 py-0.5 text-[13px]">prompt.tex</code> through the model, then starts <code className="rounded bg-slate-100 px-1 py-0.5 text-[13px]">verifier.py</code> with that model output written to standard input.
                </p>
                <p className="mt-2">
                  Read the full candidate output with <code className="rounded bg-slate-100 px-1 py-0.5 text-[13px]">sys.stdin.read()</code>. If your prompt asks the model for JSON, parse that string with <code className="rounded bg-slate-100 px-1 py-0.5 text-[13px]">json.loads(...)</code>.
                </p>
                <pre className="mt-3 overflow-x-auto rounded-lg bg-slate-950 px-4 py-3 text-sm text-slate-100">
{`import json
import sys

raw = sys.stdin.read().strip()
if not raw:
    raise SystemExit("No prompt output received on stdin.")

# For plain-text outputs:
candidate_text = raw

# For JSON outputs:
candidate = json.loads(raw)`}
                </pre>
              </section>
            </div>

            <div className="flex justify-end border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                onClick={() => setShowRuntimeHelp(false)}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
